import { forwardRef } from 'react';
import { Input, type InputProps } from '../../components/ui/Input';
import { PERCENT_STEP } from './templates-display';

export interface PercentInputProps extends Omit<InputProps, 'type' | 'value' | 'onChange'> {
  /** The value as it is **shown**: a percentage. The form stores the fraction (plan §11.6). */
  value: string;
  onValueChange: (value: string) => void;
}

/**
 * A rate, edited the way it is spoken. The stored value stays a fraction — `0.005` — and this input
 * shows and accepts `0.5`, stepping by one hundredth of a percentage point. The `%` sits inside the
 * field so a column of rates still reads as a column of numbers.
 */
export const PercentInput = forwardRef<HTMLInputElement, PercentInputProps>(({ value, onValueChange, className, ...props }, ref) => (
  // A number reads left to right in both languages, so the `%` has to sit on the same side as the
  // input's end padding — in Arabic the logical `end` would otherwise put it on top of the digits.
  <div
    dir="ltr"
    className="relative"
  >
    <Input
      ref={ref}
      type="number"
      inputMode="decimal"
      dir="ltr"
      step={PERCENT_STEP}
      className={`pe-8 text-start tabular-nums ${className ?? ''}`}
      value={value}
      onChange={(event) => onValueChange(event.target.value)}
      {...props}
    />
    <span className="pointer-events-none absolute inset-y-0 end-3 flex items-center text-xs text-muted-foreground">%</span>
  </div>
));

PercentInput.displayName = 'PercentInput';
